import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { createLocation, updateLocation } from '../../../../../api/locationApi';
import { getEntities } from '../../../../../api/entityApi';
import states from '../../../../../utils/states.json';
import { adminMenuItems } from '../../../../../config/layout';
import AlertDialog from '../../../../../components/common/AlertDialog';
import useAlertDialog from '../../../../../hooks/useAlertDialog';
import { useErrorModal } from '../../../../../hooks/useErrorModal';

const AddLocation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const locationState = useLocation();
  const isEdit = !!id;
  const orgManagementItem = adminMenuItems.find((item) => item.id === 'org-management');

  const currentBreadcrumbs = useMemo(() => {
    const currentnavItem = orgManagementItem?.subItems?.find(
      (item) => item.id === 'org-structure'
    );
    return currentnavItem?.breadcrumbs || [];
  }, [orgManagementItem]);

  const [formData, setFormData] = useState({
    locationName: '',
    entityId: '',
    timezone: 'UTC',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: 'India',
    description: '',
  });

  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const { alertDialog, showAlert, closeAlert } = useAlertDialog();
  const { showError, ErrorModal } = useErrorModal();

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const result = await getEntities();
        if (result.success) {
          setEntities(result.data.rows || result.data);
        }
      } catch (error) {
        console.error('Error fetching entities:', error);
      }
    };
    fetchEntities();

    if (isEdit) {
      const locationData = locationState.state?.locationData;
      if (locationData) {
        setFormData({
          locationName: locationData.name || locationData.locationName || '',
          entityId: locationData.entityId || '',
          timezone: locationData.timezone || 'UTC',
          address1: locationData.address1 || locationData.address || '',
          address2: locationData.address2 || '',
          city: locationData.city || '',
          state: locationData.state || '',
          zip: locationData.zip || locationData.zipCode || '',
          country: locationData.country || 'India',
          description: locationData.description || '',
        });
      } else {
        // Handle case where state is not passed (e.g. direct URL access)
        // Optionally fetch location details by ID here
        setNotification({
          open: true,
          message: 'No location data provided. Please navigate from the structure page.',
          severity: 'error'
        });
        setTimeout(() => navigate('/org-management/org-structure'), 2000);
      }
    }
  }, [isEdit, id, locationState.state, navigate]);

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === "zip") {
      value = value.replace(/\D/g, "");
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleCancel = () => {
    navigate('/org-management/org-structure');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.locationName.trim()) {
      newErrors.locationName = 'Location name is required';
    }

    if (!formData.entityId && !isEdit) {
      newErrors.entityId = 'Legal Entity is required';
    }

    if (!formData.address1.trim()) {
      newErrors.address1 = 'Address 1 is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData.zip.trim()) {
      newErrors.zip = 'Zip code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.locationName,
        entityId: formData.entityId,
        timezone: formData.timezone,
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country,
        description: formData.description,
      };

      let result;
      if (isEdit) {
        result = await updateLocation(id, payload);
      } else {
        result = await createLocation(payload);
      }

      if (result.success) {
        setNotification({
          open: true,
          message: `Location ${isEdit ? 'updated' : 'created'} successfully!`,
          severity: 'success',
        });
        setTimeout(() => {
          navigate('/org-management/org-structure');
        }, 1500);
      } else {
        showAlert({
          title: `Failed to ${isEdit ? 'Update' : 'Create'} Location`,
          message: result.message || `Failed to ${isEdit ? 'update' : 'create'} location`,
          severity: 'error'
        });
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Container maxWidth="xl" sx={{ p: 3 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: '1rem' }} />}
        sx={{
          marginBottom: 3,
          marginTop: 2,
          '& .MuiBreadcrumbs-li': {
            fontSize: '0.875rem',
          },
        }}
      >
        {currentBreadcrumbs.map((crumb) => (
          <Link
            key={crumb.path}
            onClick={() => navigate(crumb.path)}
            sx={{
              cursor: 'pointer',
              color: '#7522D1',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {crumb.label}
          </Link>
        ))}
        <Typography sx={{ color: '#030007', fontSize: '0.875rem' }}>
          {isEdit ? 'Edit Location' : 'Add Location'}
        </Typography>
      </Breadcrumbs>

      {/* Main Content */}
      <Box sx={{ marginBottom: 4 }}>
        {/* Header Section */}
        <Box
          sx={{
            backgroundColor: '#F5F5F5',
            padding: '20px 24px',
            marginBottom: 3,
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#030007',
                marginBottom: 0.5,
                fontSize: '1.1rem',
              }}
            >
              {isEdit ? 'Edit Location' : 'Add New Location'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#4F4937',
                fontSize: '0.9rem',
              }}
            >
              {isEdit
                ? 'Update your office or branch location information.'
                : 'Create a new office or branch location for your organization.'}
            </Typography>
          </Box>
        </Box>

        {/* Form Card */}
        <Card
          sx={{
            padding: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: 1,
          }}
        >
          {/* Location Details Section */}
          <Box sx={{ marginBottom: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#030007',
                marginBottom: 1,
                fontSize: '1rem',
              }}
            >
              Location Details
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#4F4937',
                marginBottom: 3,
                fontSize: '0.9rem',
              }}
            >
              Provide the essential details for the office or branch location.
            </Typography>

            {/* Grid for form fields */}
            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
              {/* Location Name */}
              <Grid item xs={12} md={6} size={{ xs: 12, sm: 6, md: 6 }}>
                <TextField
                  fullWidth
                  label="Location Name *"
                  name="locationName"
                  value={formData.locationName}
                  onChange={handleChange}
                  placeholder="e.g. Hyderabad Office"
                  variant="outlined"
                  error={!!errors.locationName}
                  helperText={errors.locationName}
                />
              </Grid>

              {/* Entity Selection (Only for Add, or read-only for edit?) */}
              {!isEdit && (
                <Grid item xs={12} md={6} size={{ xs: 12, sm: 6, md: 6 }}>
                  <FormControl fullWidth error={!!errors.entityId}>
                    <InputLabel>Legal Entity *</InputLabel>
                    <Select
                      name="entityId"
                      value={formData.entityId}
                      onChange={handleChange}
                      label="Legal Entity *"
                    >
                      <MenuItem value=""><em>Select Entity</em></MenuItem>
                      {entities.map((entity) => (
                        <MenuItem key={entity.id} value={entity.id}>
                          {entity.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.entityId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.entityId}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              )}

              {/* Timezone */}
              <Grid item xs={12} md={6} size={{ xs: 12, sm: 6, md: 6 }}>
                <TextField
                  fullWidth
                  label="Timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  placeholder="e.g. UTC, IST"
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Address Section */}
          <Box sx={{ marginBottom: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#030007',
                marginBottom: 3,
                fontSize: '1rem',
              }}
            >
              Address
            </Typography>

            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
              {/* Address 1 */}
              <Grid item xs={12} md={6} size={{ xs: 12, sm: 6, md: 6 }}>
                <TextField
                  fullWidth
                  label="Address Line 1 *"
                  name="address1"
                  value={formData.address1}
                  onChange={handleChange}
                  placeholder="Street address"
                  variant="outlined"
                  error={!!errors.address1}
                  helperText={errors.address1}
                />
              </Grid>

              {/* Address 2 */}
              <Grid item xs={12} md={6} size={{ xs: 12, sm: 6, md: 6 }}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  name="address2"
                  value={formData.address2}
                  onChange={handleChange}
                  placeholder="Apartment, suite, etc. (optional)"
                  variant="outlined"
                />
              </Grid>

              {/* City */}
              <Grid item xs={12} md={6} size={{ xs: 12, sm: 6, md: 6 }}>
                <TextField
                  fullWidth
                  label="City *"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  variant="outlined"
                  error={!!errors.city}
                  helperText={errors.city}
                />
              </Grid>

              {/* State */}
              <Grid item xs={12} md={6} size={{ xs: 12, sm: 6, md: 6 }}>
                <Autocomplete
                  fullWidth
                  options={states}
                  value={formData.state || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, state: newValue || "" }));
                    if (errors.state) {
                      setErrors(prev => ({ ...prev, state: "" }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State *"
                      placeholder="Select State"
                      error={!!errors.state}
                      helperText={errors.state}
                    />
                  )}
                />
              </Grid>

              {/* Zip */}
              <Grid item xs={12} md={6} size={{ xs: 12, sm: 6, md: 6 }}>
                <TextField
                  fullWidth
                  label="Zip/Postal Code *"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  placeholder="Zip code"
                  variant="outlined"
                  error={!!errors.zip}
                  helperText={errors.zip}
                />
              </Grid>

              {/* Country */}
              <Grid item xs={12} md={6} size={{ xs: 12, sm: 6, md: 6 }}>
                <TextField
                  fullWidth
                  label="Country *"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12} size={{ xs: 12, sm: 12, md: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter location description"
                  variant="outlined"
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 4,
              paddingTop: 3,
              borderTop: '1px solid #E8E8E8',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                borderColor: '#030007',
                color: '#030007',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 3,
                paddingY: 1.2,
                borderRadius: 1,
                '&:hover': {
                  borderColor: '#030007',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{
                backgroundColor: '#7522D1',
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 3,
                paddingY: 1.2,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#5E1BA8',
                },
              }}
            >
              {loading ? (isEdit ? 'Updating...' : 'Adding...') : (isEdit ? 'Update' : 'Add')}
            </Button>
          </Box>
        </Card>
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={closeAlert}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
        confirmText={alertDialog.confirmText}
        cancelText={alertDialog.cancelText}
        showCancel={alertDialog.showCancel}
        onConfirm={alertDialog.onConfirm}
      />

      <ErrorModal />
    </Container>
  );
};

export default AddLocation;
